import React, { useState, useEffect, useCallback } from 'react';
import {
  UsersRound, Plus, Search, X, Edit2, Trash2,
  Crown, User, ChevronRight, UserPlus, ShieldCheck
} from 'lucide-react';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE_URL;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('mideast_token')}` }
});

const STYLES = `
  .mt-header {
    display: flex; align-items: center;
    justify-content: space-between;
    margin-bottom: 24px; gap: 16px;
  }
  .mt-search-wrap {
    position: relative; max-width: 320px; margin-bottom: 20px;
  }
  .mt-search-icon {
    position: absolute; left: 12px; top: 50%;
    transform: translateY(-50%);
    color: rgba(255,255,255,0.25); pointer-events: none;
  }
  .mt-search {
    width: 100%; padding: 9px 14px 9px 38px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px; color: #E2E8F0;
    font-family: 'DM Sans', sans-serif; font-size: 13px;
    outline: none; transition: border-color 0.2s;
  }
  .mt-search:focus { border-color: rgba(14,165,233,0.35); }
  .mt-search::placeholder { color: rgba(255,255,255,0.20); }

  /* Team grid */
  .mt-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 16px;
  }

  /* Team card */
  .mt-card {
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px; overflow: hidden;
    transition: border-color 0.2s, transform 0.2s;
    cursor: pointer;
  }
  .mt-card:hover {
    border-color: rgba(14,165,233,0.22);
    transform: translateY(-2px);
  }
  .mt-card-top {
    padding: 18px 20px 14px;
    display: flex; align-items: center; gap: 14px;
    border-bottom: 1px solid rgba(255,255,255,0.05);
  }
  .mt-team-icon {
    width: 42px; height: 42px; border-radius: 12px;
    background: linear-gradient(135deg,rgba(14,165,233,0.15),rgba(245,158,11,0.10));
    border: 1px solid rgba(14,165,233,0.18);
    display: flex; align-items: center; justify-content: center;
    color: #38BDF8; flex-shrink: 0;
  }
  .mt-team-name {
    font-size: 15px; font-weight: 700; color: #F1F5F9;
    flex: 1;
  }
  .mt-card-actions {
    display: flex; gap: 4px;
  }

  /* Member list inside card */
  .mt-member-list {
    padding: 12px 20px;
  }
  .mt-member-row {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255,255,255,0.04);
  }
  .mt-member-row:last-child { border-bottom: none; }
  .mt-member-avatar {
    width: 30px; height: 30px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; flex-shrink: 0;
  }
  .mt-member-avatar-lead {
    background: rgba(245,158,11,0.15);
    color: #FCD34D;
    border: 1px solid rgba(245,158,11,0.25);
  }
  .mt-member-avatar-member {
    background: rgba(14,165,233,0.10);
    color: #38BDF8;
    border: 1px solid rgba(14,165,233,0.18);
  }
  .mt-member-name {
    font-size: 13px; font-weight: 600; color: #CBD5E0; flex: 1;
  }
  .mt-member-email {
    font-size: 10px; color: rgba(255,255,255,0.28);
  }
  .mt-role-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 2px 8px; border-radius: 20px;
    font-size: 9px; font-weight: 700;
    font-family: 'DM Mono', monospace;
    letter-spacing: 0.06em; flex-shrink: 0;
  }
  .mt-role-lead {
    background: rgba(245,158,11,0.12);
    color: #FCD34D;
    border: 1px solid rgba(245,158,11,0.20);
  }
  .mt-role-member {
    background: rgba(14,165,233,0.10);
    color: #38BDF8;
    border: 1px solid rgba(14,165,233,0.16);
  }

  .mt-card-footer {
    padding: 10px 20px;
    border-top: 1px solid rgba(255,255,255,0.05);
    display: flex; align-items: center; justify-content: space-between;
  }
  .mt-member-count {
    font-size: 11px; color: rgba(255,255,255,0.28);
    font-family: 'DM Mono', monospace;
  }

  /* Detail panel */
  .mt-panel-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.55);
    backdrop-filter: blur(4px);
    z-index: 200; animation: fadeIn 0.15s ease;
  }
  .mt-panel {
    position: fixed; top: 0; right: 0; bottom: 0;
    width: 500px; max-width: 100vw;
    background: #0D1120;
    border-left: 1px solid rgba(255,255,255,0.08);
    box-shadow: -24px 0 80px rgba(0,0,0,0.50);
    z-index: 201; display: flex; flex-direction: column;
    animation: slideIn 0.22s cubic-bezier(0.4,0,0.2,1);
    overflow: hidden;
  }
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }
  .mt-panel-header {
    padding: 20px 24px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    display: flex; align-items: center; gap: 12px;
    flex-shrink: 0;
  }
  .mt-panel-title {
    font-size: 16px; font-weight: 800; color: #F1F5F9; flex: 1;
  }
  .mt-panel-body {
    flex: 1; overflow-y: auto; padding: 22px 24px;
  }
  .mt-section-title {
    font-size: 9px; font-weight: 700;
    letter-spacing: 0.18em; text-transform: uppercase;
    color: rgba(255,255,255,0.25);
    font-family: 'DM Mono', monospace;
    margin-bottom: 12px;
  }

  /* Add member row */
  .mt-add-member-row {
    display: flex; gap: 8px; margin-bottom: 20px;
  }
  .mt-fo-select {
    flex: 1; padding: 9px 12px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 9px; color: #E2E8F0;
    font-family: 'DM Sans', sans-serif; font-size: 13px;
    outline: none; cursor: pointer;
    transition: border-color 0.2s;
  }
  .mt-fo-select:focus { border-color: rgba(14,165,233,0.35); }
  .mt-fo-select option { background: #1E2535; }
  .mt-role-select {
    padding: 9px 12px; width: 110px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 9px; color: #E2E8F0;
    font-family: 'DM Sans', sans-serif; font-size: 13px;
    outline: none; cursor: pointer;
  }
  .mt-role-select option { background: #1E2535; }

  /* Full member item in panel */
  .mt-panel-member {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 14px; border-radius: 10px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.05);
    margin-bottom: 8px;
    transition: border-color 0.2s;
  }
  .mt-panel-member:hover { border-color: rgba(255,255,255,0.10); }
  .mt-panel-member-avatar {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 700; flex-shrink: 0;
  }
  .mt-panel-member-info { flex: 1; min-width: 0; }
  .mt-panel-member-name {
    font-size: 13px; font-weight: 700; color: #E2E8F0;
    margin-bottom: 2px;
  }
  .mt-panel-member-email {
    font-size: 11px; color: rgba(255,255,255,0.30);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .mt-panel-member-mobile {
    font-size: 10px; color: rgba(255,255,255,0.22);
    font-family: 'DM Mono', monospace;
  }

  /* Empty team state */
  .mt-no-members {
    text-align: center; padding: 32px 20px;
    color: rgba(255,255,255,0.22); font-size: 13px;
  }
`;

const getInitials = (firstname = '', lastname = '') =>
  `${firstname[0] || ''}${lastname[0] || ''}`.toUpperCase() || '?';

const EMPTY_TEAM_FORM = { team_name: '' };

const MideastTeams = () => {
  const [teams,        setTeams]        = useState([]);
  const [foUsers,      setFoUsers]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [selected,     setSelected]     = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [detailLoading,setDetailLoading]= useState(false);

  // Add member state
  const [addUserId,    setAddUserId]    = useState('');
  const [addRole,      setAddRole]      = useState('member');
  const [addingMember, setAddingMember] = useState(false);
  const [memberError,  setMemberError]  = useState('');

  // Team form
  const [showForm,     setShowForm]     = useState(false);
  const [editTeam,     setEditTeam]     = useState(null);
  const [form,         setForm]         = useState(EMPTY_TEAM_FORM);
  const [saving,       setSaving]       = useState(false);
  const [formError,    setFormError]    = useState('');

  // ── Fetch ──────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [teamsRes, foRes] = await Promise.all([
        axios.get(`${BASE}/mideast/teams`,        getAuthHeader()),
        axios.get(`${BASE}/mideast/teams/fo/users`, getAuthHeader()),
      ]);
      setTeams(teamsRes.data.teams    || []);
      setFoUsers(foRes.data.fo_users  || []);
    } catch (err) {
      console.error('[Teams] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Open detail ────────────────────────────────────────────────
  const openDetail = useCallback(async (team) => {
    setSelected(team);
    setSelectedData(null);
    setMemberError('');
    setAddUserId('');
    setAddRole('member');
    setDetailLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/mideast/teams/${team.id}`,
        getAuthHeader()
      );
      setSelectedData(res.data);
    } catch (err) {
      console.error('[Teams] detail error:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // ── Add member ─────────────────────────────────────────────────
  const handleAddMember = async () => {
    if (!addUserId) { setMemberError('Select a field officer'); return; }
    setAddingMember(true); setMemberError('');
    try {
      await axios.post(
        `${BASE}/mideast/teams/${selected.id}/members`,
        { user_id: parseInt(addUserId), role: addRole },
        getAuthHeader()
      );
      setAddUserId(''); setAddRole('member');
      // Refresh detail
      const res = await axios.get(
        `${BASE}/mideast/teams/${selected.id}`,
        getAuthHeader()
      );
      setSelectedData(res.data);
      fetchAll();
    } catch (err) {
      setMemberError(err?.response?.data?.error || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  // ── Remove member ──────────────────────────────────────────────
  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the team?')) return;
    try {
      await axios.delete(
        `${BASE}/mideast/teams/${selected.id}/members/${userId}`,
        getAuthHeader()
      );
      const res = await axios.get(
        `${BASE}/mideast/teams/${selected.id}`,
        getAuthHeader()
      );
      setSelectedData(res.data);
      fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to remove member');
    }
  };

  // ── Create / Edit team ─────────────────────────────────────────
  const openCreate = () => {
    setEditTeam(null);
    setForm(EMPTY_TEAM_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (team, e) => {
    e.stopPropagation();
    setEditTeam(team);
    setForm({ team_name: team.team_name });
    setFormError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.team_name.trim()) {
      setFormError('Team name is required');
      return;
    }
    setSaving(true); setFormError('');
    try {
      if (editTeam) {
        await axios.put(
          `${BASE}/mideast/teams/${editTeam.id}`,
          form, getAuthHeader()
        );
      } else {
        await axios.post(
          `${BASE}/mideast/teams`,
          form, getAuthHeader()
        );
      }
      setShowForm(false);
      fetchAll();
    } catch (err) {
      setFormError(err?.response?.data?.error || 'Failed to save team');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async (team, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete team "${team.team_name}"?`)) return;
    try {
      await axios.delete(`${BASE}/mideast/teams/${team.id}`, getAuthHeader());
      fetchAll();
      if (selected?.id === team.id) setSelected(null);
    } catch (err) {
      alert(err?.response?.data?.error || 'Delete failed');
    }
  };

  // ── FO users not already in this team ─────────────────────────
  const availableFOs = foUsers.filter(u =>
    !selectedData?.members?.some(m => m.id === u.id)
  );

  // ── Filtered teams ─────────────────────────────────────────────
  const filtered = teams.filter(t =>
    t.team_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <style>{STYLES}</style>

      {/* Header */}
      <div className="mt-header">
        <div>
          <div className="md-page-title">Teams</div>
          <div className="md-page-sub">
            {teams.length} team{teams.length !== 1 ? 's' : ''} · {foUsers.length} field officers
          </div>
        </div>
        <button className="md-btn md-btn-primary" onClick={openCreate}>
          <Plus size={14}/> New Team
        </button>
      </div>

      {/* Search */}
      <div className="mt-search-wrap">
        <Search size={14} className="mt-search-icon"/>
        <input
          className="mt-search"
          placeholder="Search teams…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="mt-grid">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="md-skeleton"
              style={{ height: 200, borderRadius: 14 }}/>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="md-empty">
          <div className="md-empty-icon"><UsersRound size={28}/></div>
          {search ? 'No teams match your search' : 'No teams yet — create your first one'}
        </div>
      ) : (
        <div className="mt-grid">
          {filtered.map(team => (
            <div
              key={team.id}
              className="mt-card"
              onClick={() => openDetail(team)}
            >
              <div className="mt-card-top">
                <div className="mt-team-icon">
                  <UsersRound size={18} strokeWidth={1.8}/>
                </div>
                <div className="mt-team-name">{team.team_name}</div>
                <div className="mt-card-actions">
                  <button
                    className="md-btn md-btn-ghost"
                    style={{ padding: '5px 8px' }}
                    onClick={e => openEdit(team, e)}
                  >
                    <Edit2 size={12}/>
                  </button>
                  <button
                    className="md-btn md-btn-danger"
                    style={{ padding: '5px 8px' }}
                    onClick={e => handleDeleteTeam(team, e)}
                  >
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>

              {/* Preview members */}
              <div className="mt-member-list">
                {!team.members || team.members.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.22)', padding: '8px 0' }}>
                    No members yet
                  </div>
                ) : team.members.slice(0, 3).map(member => (
                  <div key={member.id} className="mt-member-row">
                    <div className={`mt-member-avatar ${member.role === 'lead' ? 'mt-member-avatar-lead' : 'mt-member-avatar-member'}`}>
                      {getInitials(member.firstname, member.lastname)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="mt-member-name">
                        {member.firstname} {member.lastname}
                      </div>
                      <div className="mt-member-email">{member.email}</div>
                    </div>
                    <span className={`mt-role-badge ${member.role === 'lead' ? 'mt-role-lead' : 'mt-role-member'}`}>
                      {member.role === 'lead'
                        ? <><Crown size={8}/> Lead</>
                        : <><User size={8}/> Member</>
                      }
                    </span>
                  </div>
                ))}
                {team.members?.length > 3 && (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', paddingTop: 6 }}>
                    +{team.members.length - 3} more
                  </div>
                )}
              </div>

              <div className="mt-card-footer">
                <span className="mt-member-count">
                  {team.members?.length || 0} member{team.members?.length !== 1 ? 's' : ''}
                </span>
                <ChevronRight size={14} style={{ color: 'rgba(255,255,255,0.20)' }}/>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Detail Panel ─────────────────────────────────────── */}
      {selected && (
        <>
          <div className="mt-panel-overlay" onClick={() => setSelected(null)}/>
          <div className="mt-panel">
            <div className="mt-panel-header">
              <div style={{
                width: 40, height: 40, borderRadius: 11,
                background: 'linear-gradient(135deg,rgba(14,165,233,0.15),rgba(245,158,11,0.10))',
                border: '1px solid rgba(14,165,233,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#38BDF8', flexShrink: 0,
              }}>
                <UsersRound size={18}/>
              </div>
              <div style={{ flex: 1 }}>
                <div className="mt-panel-title">{selected.team_name}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.28)', marginTop: 2 }}>
                  {selectedData?.members?.length || 0} member{selectedData?.members?.length !== 1 ? 's' : ''}
                </div>
              </div>
              <button className="md-modal-close" onClick={() => setSelected(null)}>
                <X size={18}/>
              </button>
            </div>

            <div className="mt-panel-body">
              {detailLoading ? (
                <>
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="md-skeleton"
                      style={{ height: 60, borderRadius: 10, marginBottom: 10 }}/>
                  ))}
                </>
              ) : (
                <>
                  {/* Add member */}
                  <div style={{ marginBottom: 24 }}>
                    <div className="mt-section-title">Add Member</div>
                    <div className="mt-add-member-row">
                      <select
                        className="mt-fo-select"
                        value={addUserId}
                        onChange={e => setAddUserId(e.target.value)}
                      >
                        <option value="">Select field officer…</option>
                        {availableFOs.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.firstname} {u.lastname}
                            {u.team_name ? ` (${u.team_name})` : ''}
                          </option>
                        ))}
                      </select>
                      <select
                        className="mt-role-select"
                        value={addRole}
                        onChange={e => setAddRole(e.target.value)}
                      >
                        <option value="member">Member</option>
                        <option value="lead">Lead</option>
                      </select>
                      <button
                        className="md-btn md-btn-primary"
                        style={{ flexShrink: 0 }}
                        onClick={handleAddMember}
                        disabled={addingMember}
                      >
                        {addingMember
                          ? <span className="md-spinner"/>
                          : <UserPlus size={14}/>
                        }
                      </button>
                    </div>
                    {memberError && (
                      <div className="md-alert md-alert-error" style={{ marginBottom: 0 }}>
                        ⚠ {memberError}
                      </div>
                    )}
                  </div>

                  {/* Members list */}
                  <div className="mt-section-title">
                    Team Members ({selectedData?.members?.length || 0})
                  </div>

                  {!selectedData?.members?.length ? (
                    <div className="mt-no-members">
                      <UsersRound size={28} style={{ opacity: 0.25, marginBottom: 10 }}/>
                      <div>No members yet — add your first one above</div>
                    </div>
                  ) : (
                    // Lead first, then members
                    [...(selectedData?.members || [])]
                      .sort((a, b) => a.role === 'lead' ? -1 : 1)
                      .map(member => (
                        <div key={member.id} className="mt-panel-member">
                          <div className={`mt-panel-member-avatar ${member.role === 'lead' ? 'mt-member-avatar-lead' : 'mt-member-avatar-member'}`}>
                            {getInitials(member.firstname, member.lastname)}
                          </div>
                          <div className="mt-panel-member-info">
                            <div className="mt-panel-member-name">
                              {member.firstname} {member.lastname}
                            </div>
                            <div className="mt-panel-member-email">{member.email}</div>
                            {member.mobile && (
                              <div className="mt-panel-member-mobile">{member.mobile}</div>
                            )}
                          </div>
                          <span className={`mt-role-badge ${member.role === 'lead' ? 'mt-role-lead' : 'mt-role-member'}`}>
                            {member.role === 'lead'
                              ? <><Crown size={8}/> Lead</>
                              : <><User size={8}/> Member</>
                            }
                          </span>
                          <button
                            className="md-btn md-btn-danger"
                            style={{ padding: '5px 8px', marginLeft: 8 }}
                            onClick={() => handleRemoveMember(member.id)}
                          >
                            <X size={11}/>
                          </button>
                        </div>
                      ))
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Create / Edit Modal ──────────────────────────────── */}
      {showForm && (
        <div className="md-modal-overlay">
          <div className="md-modal" style={{ maxWidth: 400 }}>
            <div className="md-modal-header">
              <ShieldCheck size={16} color="rgba(14,165,233,0.70)" strokeWidth={1.8}/>
              <div className="md-modal-title">
                {editTeam ? 'Rename Team' : 'New Team'}
              </div>
              <button className="md-modal-close" onClick={() => setShowForm(false)}>
                <X size={18}/>
              </button>
            </div>
            <div className="md-modal-body">
              {formError && (
                <div className="md-alert md-alert-error">⚠ {formError}</div>
              )}
              <div className="md-field">
                <label className="md-label">Team Name *</label>
                <input
                  className="md-input"
                  placeholder="e.g. Alpha Crew"
                  value={form.team_name}
                  onChange={e => setForm({ team_name: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                  autoFocus
                />
              </div>
            </div>
            <div className="md-modal-footer">
              <button
                className="md-btn md-btn-ghost"
                onClick={() => setShowForm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="md-btn md-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? <><span className="md-spinner"/> Saving…</>
                  : editTeam ? 'Rename' : 'Create Team'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MideastTeams;